/** Outcomes understood by both online payment applications. */
export type PaymentOutcome = 'Succeeded' | 'NotCharged' | 'Declined' | 'Voided' | 'RecordedNeedsReview' | 'Unresolved'

const outcomes: PaymentOutcome[] = ['Succeeded', 'NotCharged', 'Declined', 'Voided', 'RecordedNeedsReview', 'Unresolved']

/** Reads additive API fields and treats older, unclassified failures as uncertain. */
export function readPaymentSafety(data: Record<string, unknown>) {
  const success = (data.success ?? data.Success) === true
  const rawOutcome = data.outcome ?? data.Outcome
  let outcome: PaymentOutcome = typeof rawOutcome === 'string' && outcomes.includes(rawOutcome as PaymentOutcome)
    ? rawOutcome as PaymentOutcome
    : success ? 'Succeeded' : 'Unresolved'
  if (!success && (outcome === 'Succeeded' || outcome === 'RecordedNeedsReview')) outcome = 'Unresolved'
  const balance = data.newAccountBalance ?? data.NewAccountBalance
  return {
    outcome,
    requiresRetryConfirmation: outcome === 'Unresolved' || (data.requiresRetryConfirmation ?? data.RequiresRetryConfirmation) === true,
    accountBalanceAvailable: (data.accountBalanceAvailable ?? data.AccountBalanceAvailable) !== false &&
      typeof balance === 'number' && Number.isFinite(balance)
  }
}

export const uncertainPaymentMessage = 'We could not confirm the final payment status. The original payment may still be charged. Another attempt could create an additional charge. Please check with the publication before paying again.'

/** Keeps earlier uncertainty visible for this screen, even after a later decline or void. */
export class PaymentRetrySafety {
  hasUnresolvedAttempt = false
  acknowledged = false
  unresolvedTransactionIds: string[] = []

  /** Whether the user has acknowledged the risk for the next submission. */
  get canSubmit(): boolean {
    return !this.hasUnresolvedAttempt || this.acknowledged
  }

  /** Consumes acknowledgment once, before either saved-method or tokenized submission. */
  beginAttempt(): boolean {
    if (!this.canSubmit) return false
    this.acknowledged = false
    return true
  }

  /** Records a result without clearing uncertainty from an earlier attempt. */
  recordResult(result: { success: boolean; outcome?: string; requiresRetryConfirmation?: boolean; transactionId?: string }): void {
    if (result.requiresRetryConfirmation || (!result.success &&
      (!result.outcome || !outcomes.includes(result.outcome as PaymentOutcome) || result.outcome === 'Unresolved'))) {
      this.recordUncertain(result.transactionId)
    }
  }

  /** Requires a fresh acknowledgment after a lost or unclassified response. */
  recordUncertain(transactionId?: string): void {
    this.hasUnresolvedAttempt = true
    this.acknowledged = false
    const reference = transactionId?.trim()
    if (reference && !this.unresolvedTransactionIds.includes(reference)) {
      this.unresolvedTransactionIds.push(reference)
    }
  }
}
